import { MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { CONTACT } from "@/constants/contact";
import { Button } from "@/components/ui/button";
import { SERVICES } from "@/constants/services";
import { CITY_LIST } from "@/constants/cities";
import { scrollToSection } from "@/lib/lenisStore";
import { COPY } from "@/constants/copy";

const COMPANY_LINKS = [
  { label: "About", id: "about" },
  { label: "Reviews", id: "testimonials" },
  { label: "FAQ", id: "faq" },
];

export const Footer = ({ onBook }) => (
  <footer data-testid="site-footer" className="bg-navy-deep text-white">
    <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl py-16 sm:py-20 border-b border-white/10 grid grid-cols-12 gap-y-6 gap-x-4 md:gap-8 items-center">
      <div className="col-span-12 lg:col-span-8">
        <p className="overline-label mb-4">Something broken?</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
          Straight advice and an estimate — fifty bucks.
        </h2>
      </div>
      <div className="col-span-12 lg:col-span-4 flex flex-wrap lg:justify-end gap-4">
        <Button
          data-testid="footer-book-button"
          data-book-cta
          onClick={() => onBook(null)}
          className="bg-terracotta hover:bg-terracotta-hover text-white font-semibold px-8 h-12 hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow,background-color]"
        >
          Book online <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <a
          href={CONTACT.smsHref}
          data-testid="footer-emergency-call"
          className="inline-flex items-center gap-2 border border-white/30 px-6 h-12 font-semibold hover:bg-white hover:text-navy transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> {COPY.textPhoto}
        </a>
      </div>
    </div>

    <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl py-14 grid grid-cols-12 gap-y-8 gap-x-4 md:gap-10">
      <div className="col-span-12 md:col-span-5">
        <div className="flex items-center gap-3">
          <LogoMark className="h-10 w-10" onDark />
          <div className="leading-none">
            <span className="block font-display font-extrabold tracking-tight text-lg">Solid Home Services</span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.3em] mt-1 text-white/50">
              Repairs & Installs
            </span>
          </div>
        </div>
        <p className="mt-5 text-white/60 text-sm leading-relaxed max-w-sm">
          One man, every trade under your roof. {COPY.trust}, and guaranteed for as long as you own the home.
        </p>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-white/40">
          Proudly serving {CONTACT.area}
        </p>
      </div>

      <div className="col-span-6 md:col-span-3">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Services</p>
        <ul className="space-y-2.5">
          {SERVICES.map((s) => (
            <li key={s.id}>
              <button
                data-testid={`footer-service-${s.id}`}
                onClick={() => scrollToSection("services")}
                className="text-left text-sm text-white/70 hover:text-terracotta transition-colors"
              >
                {s.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="col-span-6 md:col-span-4">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Contact</p>
        <ul className="space-y-2.5 text-sm text-white/70">
          <li>
            <button
              type="button"
              data-testid="footer-contact-book"
              data-book-cta
              onClick={() => onBook(null)}
              className="font-semibold text-terracotta hover:text-white transition-colors"
            >
              Book online →
            </button>
          </li>
          <li>
            <a href={CONTACT.smsHref} data-testid="footer-contact-text" className="hover:text-terracotta transition-colors">
              {COPY.textPhoto}
            </a>
          </li>
          <li>
            <a href={`mailto:${CONTACT.email}`} className="break-all hover:text-terracotta transition-colors">
              {CONTACT.email}
            </a>
          </li>
          <li>{CONTACT.hours}</li>
        </ul>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40 mt-8 mb-4">Company</p>
        <ul className="space-y-2.5 text-sm text-white/70" data-testid="footer-company-links">
          {COMPANY_LINKS.map((l) => (
            <li key={l.id}>
              <button
                type="button"
                data-testid={`footer-link-${l.id}`}
                onClick={() => scrollToSection(l.id)}
                className="text-left hover:text-terracotta transition-colors"
              >
                {l.label}
              </button>
            </li>
          ))}
          <li>
            <Link to="/portal" data-testid="footer-link-portal" className="hover:text-terracotta transition-colors">
              Customer portal
            </Link>
          </li>
          <li>
            <a
              href="https://g.page/r/CQq3-foBVx4iEAI/review"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-review-link"
              className="hover:text-terracotta transition-colors"
            >
              Leave a Google review
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl py-8 flex flex-wrap items-center gap-x-6 gap-y-3">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">Areas we serve</p>
        <nav className="flex flex-wrap gap-x-5 gap-y-2" data-testid="footer-cities">
          {CITY_LIST.map((c) => (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              data-testid={`footer-city-${c.slug}`}
              className="text-sm text-white/70 hover:text-terracotta transition-colors"
            >
              {c.name}
            </Link>
          ))}
        </nav>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl pt-6 pb-24 md:pb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-white/40">
          © 2026 Solid Home Services ·{" "}
          <a
            href="https://secure.lni.wa.gov/verify/Detail.aspx?UBI=606272681&LIC=SOLIDHS742NQ&SAW="
            target="_blank"
            rel="noreferrer"
            data-testid="footer-license-link"
            className="underline decoration-dotted underline-offset-4 hover:text-terracotta transition-colors"
          >
            WA License #SOLIDHS742NQ
          </a>{" "}
          · {COPY.trust}.
        </p>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">Repairs & Installs</p>
      </div>
    </div>
  </footer>
);
